import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Stack,
  Alert,
  CircularProgress,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  TextField,
} from '@mui/material';
import {
  Event as CalendarIcon,
  AccessTime as TimeIcon,
  LocationOn as LocationIcon,
  Group as GroupIcon,
  HowToReg as RegisterIcon,
  PersonRemove as UnregisterIcon,
  Edit as EditIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from '../utils/axios';
import { getImageUrl } from '../utils/image';
import { useSnackbar } from 'notistack';
import { useAuth } from '../context/AuthContext';
import ModifyParticipantsDialog from '../components/activities/ModifyParticipantsDialog';
import HtmlContent from '../components/HtmlContent';
import { isActivityFull, getSpotsLabel, getAvailableSpots, getParticipantsText, isIndividualSession } from '../utils/activity';
import { canCancelRegistration, cancellationCutoffMessage, CANCELLATION_CUTOFF_HOURS } from '../utils/cancellationPolicy';

const ActivityDetails = () => {
  const { t } = useTranslation();
  const { id: idOrSlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activity, setActivity] = useState(null);
  const [userRegistration, setUserRegistration] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState('register');
  const { enqueueSnackbar } = useSnackbar();
  const [registrationData, setRegistrationData] = useState({
    numberOfParticipants: 1,
    useSubscription: true,
    treatmentType: ''
  });
  const [openModifyDialog, setOpenModifyDialog] = useState(false);
  const [newNumberOfParticipants, setNewNumberOfParticipants] = useState(1);
  const [modifyError, setModifyError] = useState('');
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const fetchActivityData = useCallback(async () => {
    try {
      setLoading(true);
      const activityRes = await axios.get(`/api/activities/${idOrSlug}`);
      
      if (activityRes.data) {
        setActivity(activityRes.data);
        
        if (user) {
          try {
            const registrationRes = await axios.get(`/api/activities/${idOrSlug}/registration`);
            setUserRegistration(registrationRes.data);
          } catch (regError) {
            setUserRegistration(null);
            setError(t('errorFetchingRegistration'));
          }
        } else {
          setUserRegistration(null);
        }
        
        setError(null);
      } else {
        setError(t('activityNotFound'));
      }
    } catch (err) {
      setError(err.response?.data?.message || t('errorFetchingActivity'));
    } finally {
      setLoading(false);
    }
  }, [idOrSlug, user, t]);

  useEffect(() => {
    fetchActivityData();
  }, [fetchActivityData]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleOpenDialog = (type) => {
    setDialogType(type);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleRegister = async () => {
    if (!user) {
      navigate('/login', { state: { from: location.pathname } });
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`/api/activities/${idOrSlug}/register`, {
        numberOfParticipants: registrationData.numberOfParticipants,
        useSubscription: true,
        treatmentType: activity.category === 'Soin' ? registrationData.treatmentType : undefined
      });
      
      if (response.data) {
        handleCloseDialog();
        enqueueSnackbar(t('registrationSuccess'), { variant: 'success' });

        // Get the activity by ID to ensure we get fresh data
        const activityRes = await axios.get(`/api/activities/${activity._id}`);
        await axios.get('/api/users/me');
        
        if (activityRes.data) {
          setActivity(activityRes.data);
          const registrationRes = await axios.get(`/api/activities/${idOrSlug}/registration`);
          setUserRegistration(registrationRes.data);
        }

        window.dispatchEvent(new CustomEvent('userDataChanged'));
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || t('registrationError');
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleUnregister = async () => {
    try {
      setLoading(true);
      await axios.delete(`/api/activities/${idOrSlug}/register`);
      await Promise.all([
        fetchActivityData(),
        axios.get('/api/users/me')
      ]);
      handleCloseDialog();
      enqueueSnackbar(t('unregistrationSuccess'), { variant: 'success' });

      window.dispatchEvent(new CustomEvent('userDataChanged'));
    } catch (error) {
      const errorMessage = error.response?.data?.message || t('unregistrationError');
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleModifyParticipants = async (newNumberOfParticipants, useSubscription) => {
    try {
      setModifyError('');
      const response = await axios.put(`/api/activities/${idOrSlug}/participants`, {
        newNumberOfParticipants,
        useSubscription
      });

      // Update the activity data
      const activityRes = await axios.get(`/api/activities/${idOrSlug}`);
      setActivity(activityRes.data);
      
      // Update user's remaining activities if needed
      if (response.data.remainingActivities !== undefined) {
        window.dispatchEvent(new CustomEvent('userDataChanged'));
      }

      setOpenModifyDialog(false);
      enqueueSnackbar(t('participantsModifiedSuccess'), { variant: 'success' });
    } catch (error) {
      setModifyError(error.response?.data?.message || t('errorModifyingParticipants'));
    }
  };

  const renderRegistrationSection = () => {
    if (!activity) return null;

    const isFull = isActivityFull(activity);
    const availableSpots = getAvailableSpots(activity);
    const isRegistered = userRegistration?.registered;

    // If user is not logged in, show login prompt
    if (!user) {
      return (
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<RegisterIcon />}
            onClick={() => navigate('/login', { state: { from: location.pathname } })}
            disabled={isFull}
            sx={{ 
              minWidth: 200,
              py: 1.5
            }}
          >
            {isFull ? t('activityFull') : t('loginToRegister')}
          </Button>
          {!isFull && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t('loginPromptMessage')}
            </Typography>
          )}
        </Box>
      );
    }

    // For logged-in users, show registration/unregistration buttons
    return (
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        {isRegistered ? (
          <>
            <Button
              variant="outlined"
              color="error"
              startIcon={<UnregisterIcon />}
              onClick={() => handleOpenDialog('unregister')}
              sx={{ mr: 1, minWidth: 150 }}
            >
              {t('unregister')}
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<EditIcon />}
              onClick={() => setOpenModifyDialog(true)}
              sx={{ minWidth: 150 }}
            >
              {t('modifyParticipants')}
            </Button>
          </>
        ) : (
          <Stack spacing={2} alignItems="center">
            <Button
              variant="contained"
              color="primary"
              startIcon={<RegisterIcon />}
              onClick={() => handleOpenDialog('register')}
              disabled={isFull}
              sx={{ minWidth: 200 }}
            >
              {isFull ? t('activityFull') : t('register')}
            </Button>
          </Stack>
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!activity) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">{t('activityNotFound')}</Alert>
      </Container>
    );
  }

  const isRegistered = userRegistration?.registered;
  const isFull = isActivityFull(activity);
  const isPast = new Date(activity.date) < new Date();
  const canCancel = canCancelRegistration(activity);
  const availableSpots = getAvailableSpots(activity);

  return (
    <Container maxWidth="lg" sx={{ 
      mt: { xs: 2, md: 4 }, 
      mb: { xs: 2, md: 4 },
      px: { xs: 2, md: 3 }
    }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper elevation={0} sx={{ 
        p: { xs: 2, md: 4 },
        '@media (max-width: 768px)': {
          boxShadow: 'none',
          backgroundColor: 'transparent'
        }
      }}>
        <Grid container spacing={{ xs: 2, md: 4 }}>
          {activity.images?.[0] && (
            <Grid item xs={12}>
              <Box
                component="img"
                src={getImageUrl(activity.images[0].url)}
                alt={activity.images[0].alt || activity.title}
                sx={{
                  width: '100%',
                  height: { 
                    xs: 200, 
                    sm: 300,
                    md: 500  // Increased height for desktop
                  },
                  objectFit: { 
                    xs: 'cover',
                    md: 'contain'  // Changed to contain for desktop
                  },
                  objectPosition: 'center',
                  borderRadius: 1,
                  bgcolor: 'grey.100'  // Light background to make image visible
                }}
              />
            </Grid>
          )}

          <Grid item xs={12} md={8}>
            <Stack spacing={{ xs: 2, md: 4 }}>
              <Box>
                <Typography 
                  variant="h4" 
                  gutterBottom
                  sx={{
                    fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                    lineHeight: { xs: 1.3, md: 1.5 },
                    mb: { xs: 1, md: 2 }
                  }}
                >
                  {activity.title}
                </Typography>
                <Stack 
                  direction="row" 
                  spacing={1} 
                  sx={{ 
                    flexWrap: 'wrap', 
                    gap: 1,
                    mb: { xs: 2, md: 3 }
                  }}
                >
                  <Chip
                    label={activity.category}
                    color="primary"
                    size="small"
                    sx={{ 
                      fontSize: { xs: '0.75rem', md: '0.875rem' },
                      height: 24
                    }}
                  />
                  <Chip
                    label={isPast ? t('past') : t('upcoming')}
                    color={isPast ? 'default' : 'success'}
                    size="small"
                    sx={{ 
                      fontSize: { xs: '0.75rem', md: '0.875rem' },
                      height: 24
                    }}
                  />
                  <Chip
                    label={`${availableSpots} ${t('placesAvailable')}`}
                    sx={{ 
                      fontSize: { xs: '0.75rem', md: '0.875rem' },
                      height: 24,
                      backgroundColor: isFull ? 'error.main' : 'primary.main',
                      color: '#fff',
                      '& .MuiChip-label': {
                        color: 'inherit'
                      }
                    }}
                    size="small"
                  />
                </Stack>
                <Typography 
                  variant="body1" 
                  color="text.secondary"
                  sx={{
                    fontSize: { xs: '0.875rem', md: '1rem' },
                    mb: { xs: 2, md: 3 }
                  }}
                >
                  {activity.shortDescription}
                </Typography>
              </Box>

              <Stack spacing={{ xs: 1.5, md: 2 }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  gap: { xs: 1, md: 2 }
                }}>
                  <CalendarIcon sx={{ 
                    fontSize: { xs: '1.25rem', md: '1.5rem' },
                    color: 'primary.main' 
                  }} />
                  <Typography 
                    variant="body1"
                    sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}
                  >
                    {format(new Date(activity.date), 'EEEE d MMMM yyyy', { locale: fr })}
                  </Typography>
                </Box>

                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  gap: { xs: 1, md: 2 }
                }}>
                  <TimeIcon sx={{ 
                    fontSize: { xs: '1.25rem', md: '1.5rem' },
                    color: 'primary.main' 
                  }} />
                  <Typography 
                    variant="body1"
                    sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}
                  >
                    {format(new Date(activity.date), 'HH:mm', { locale: fr })} - {activity.duration} {t('minutes')}
                  </Typography>
                </Box>

                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  gap: { xs: 1, md: 2 }
                }}>
                  <LocationIcon sx={{ 
                    fontSize: { xs: '1.25rem', md: '1.5rem' },
                    color: 'primary.main' 
                  }} />
                  <Typography 
                    variant="body1"
                    sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}
                  >
                    {typeof activity.location === 'object' 
                      ? `${activity.location.name}, ${activity.location.address}, ${activity.location.city}`
                      : activity.location}
                  </Typography>
                </Box>

                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  gap: { xs: 1, md: 2 }
                }}>
                  <GroupIcon sx={{ 
                    fontSize: { xs: '1.25rem', md: '1.5rem' },
                    color: 'primary.main' 
                  }} />
                  <Typography 
                    variant="body1"
                    sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}
                  >
                    {getParticipantsText(activity, t)}
                  </Typography>
                </Box>
              </Stack>

              <Box sx={{ mt: { xs: 2, md: 3 } }}>
                <Typography 
                  variant="h6" 
                  gutterBottom
                  sx={{
                    fontSize: { xs: '1.125rem', md: '1.25rem' },
                    mb: { xs: 1, md: 2 }
                  }}
                >
                  {t('description')}
                </Typography>
                <Box
                  sx={{
                    maxHeight: isMobile && !isDescriptionExpanded ? 140 : 'none',
                    overflow: 'hidden',
                    position: 'relative',
                  }}
                >
                  <HtmlContent
                    sx={{
                      fontSize: { xs: '0.875rem', md: '1rem' },
                      color: 'text.primary',
                    }}
                  >
                    {activity.description}
                  </HtmlContent>
                </Box>
                {isMobile && (activity.description || '').length > 150 && (
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-block',
                      color: 'primary.main',
                      mt: 1,
                      cursor: 'pointer',
                      fontWeight: 500,
                      '&:hover': { textDecoration: 'underline' },
                    }}
                    onClick={() => setIsDescriptionExpanded((v) => !v)}
                  >
                    {isDescriptionExpanded ? `... ${t('less')}` : `... ${t('more') || 'plus'}`}
                  </Box>
                )}
              </Box>
            </Stack>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper 
              elevation={3} 
              sx={{ 
                p: { xs: 2, md: 3 },
                position: 'sticky',
                top: 24,
                '@media (max-width: 768px)': {
                  position: 'static',
                  mt: 2
                }
              }}
            >
              <Stack spacing={2}>
                <Box>
                  <Typography 
                    variant="h6" 
                    gutterBottom
                    sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}
                  >
                    {t('category')}
                  </Typography>
                  <Chip 
                    label={activity.category} 
                    color="primary" 
                    sx={{ 
                      fontSize: { xs: '0.75rem', md: '0.875rem' },
                      height: { xs: 24, md: 32 }
                    }} 
                  />
                </Box>

                <Box>
                  <Typography 
                    variant="h6" 
                    gutterBottom
                    sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}
                  >
                    {t('availability')}
                  </Typography>
                  <Typography 
                    variant="body1"
                    sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}
                  >
                    {getSpotsLabel(activity, t)}
                  </Typography>
                </Box>

                {!user && (
                  <Box sx={{ textAlign: 'center' }}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      gap: 1,
                      mb: 2,
                      bgcolor: 'info.lighter',
                      p: 1.5,
                      borderRadius: 1
                    }}>
                      <InfoIcon sx={{ 
                        color: 'info.main',
                        fontSize: { xs: '1.25rem', md: '1.5rem' }
                      }} />
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                      >
                        {t('loginToSeeMore')}
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => navigate('/login', { state: { from: location.pathname } })}
                      fullWidth
                      sx={{ 
                        py: { xs: 1, md: 1.5 },
                        fontSize: { xs: '0.875rem', md: '1rem' }
                      }}
                    >
                      {t('login')}
                    </Button>
                  </Box>
                )}

                {user && (
                  <Box sx={{ mt: { xs: 1, md: 2 } }}>
                    {isRegistered ? (
                      <Stack spacing={1}>
                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={<UnregisterIcon />}
                          onClick={() => handleOpenDialog('unregister')}
                          fullWidth
                          disabled={isPast || !canCancel}
                          sx={{ 
                            fontSize: { xs: '0.875rem', md: '1rem' },
                            py: { xs: 1, md: 1.5 }
                          }}
                        >
                          {t('unregister')}
                        </Button>
                        {!isPast && !canCancel && (
                          <Alert severity="info" sx={{ py: 0.5 }}>
                            {cancellationCutoffMessage(activity) ||
                              t('cancellationCutoffHours', { hours: CANCELLATION_CUTOFF_HOURS })}
                          </Alert>
                        )}
                        <Button
                          variant="outlined"
                          startIcon={<EditIcon />}
                          onClick={() => setOpenModifyDialog(true)}
                          fullWidth
                          disabled={isPast}
                          sx={{ 
                            fontSize: { xs: '0.875rem', md: '1rem' },
                            py: { xs: 1, md: 1.5 }
                          }}
                        >
                          {t('modifyParticipants')}
                        </Button>
                      </Stack>
                    ) : (
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<RegisterIcon />}
                        onClick={() => handleOpenDialog('register')}
                        fullWidth
                        disabled={isFull || isPast}
                        sx={{ 
                          fontSize: { xs: '0.875rem', md: '1rem' },
                          py: { xs: 1, md: 1.5 }
                        }}
                      >
                        {t('register')}
                      </Button>
                    )}
                  </Box>
                )}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      {/* Registration Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            '@media (max-width: 768px)': {
              margin: 2,
              width: 'calc(100% - 32px)',
              maxHeight: 'calc(100% - 32px)'
            }
          }
        }}
      >
        <DialogTitle sx={{ 
          fontSize: { xs: '1.25rem', md: '1.5rem' },
          py: { xs: 1.5, md: 2 }
        }}>
          {dialogType === 'register' ? t('registerActivity') : t('unregisterActivity')}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2, minWidth: { xs: 'auto', sm: 300 } }}>
            {dialogType === 'register' && (
              <>
                {activity.category === 'Soin' && (
                  <FormControl fullWidth required>
                    <InputLabel>{t('treatmentType')}</InputLabel>
                    <Select
                      value={registrationData.treatmentType}
                      onChange={(e) => setRegistrationData(prev => ({
                        ...prev,
                        treatmentType: e.target.value
                      }))}
                      label={t('treatmentType')}
                    >
                      <MenuItem value="CNT">Chi Nei Tsang</MenuItem>
                      <MenuItem value="Shiatsu">Shiatsu</MenuItem>
                      <MenuItem value="Massage à l'huile">Massage à l'huile</MenuItem>
                    </Select>
                  </FormControl>
                )}

                <FormControl fullWidth>
                  <InputLabel>{t('numberOfParticipants')}</InputLabel>
                  <Select
                    value={registrationData.numberOfParticipants}
                    onChange={(e) => setRegistrationData(prev => ({
                      ...prev,
                      numberOfParticipants: e.target.value
                    }))}
                    label={t('numberOfParticipants')}
                    sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}
                  >
                    {(activity.category === 'Shiatsu' || activity.category === 'Chi Nei Tsang' || activity.category === 'Soin'
                      ? [1] 
                      : [...Array(Math.min(availableSpots, 10))].map((_, i) => i + 1)
                    ).map(num => (
                      <MenuItem 
                        key={num} 
                        value={num}
                        sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}
                      >
                        {num} {t(num === 1 ? 'participant' : 'participants')}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {activity.category !== 'Shiatsu' && activity.category !== 'Chi Nei Tsang' && activity.category !== 'Soin' && (
                  <FormControlLabel
                    control={
                      <Switch
                        checked
                        disabled
                      />
                    }
                    label={t('useSubscription')}
                    sx={{ 
                      '& .MuiFormControlLabel-label': {
                        fontSize: { xs: '0.875rem', md: '1rem' }
                      }
                    }}
                  />
                )}

                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}
                >
                  {t('availableSpots')}: {availableSpots}
                </Typography>

                {user?.remainingActivities !== undefined && !isIndividualSession(activity.category) && (
                  <>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}
                    >
                      Activités restantes ({user.remainingActivities})
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}
                    >
                      {`(Le nombre d'activités disponibles diminuera de ${registrationData.numberOfParticipants} participant${registrationData.numberOfParticipants > 1 ? 's' : ''})`}
                    </Typography>
                  </>
                )}
              </>
            )}

            <Typography 
              variant="body1"
              sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}
            >
              {dialogType === 'register'
                ? t('confirmRegistrationText', {
                    activity: activity.title,
                    date: format(new Date(activity.date), 'EEEE d MMMM', { locale: fr }),
                    time: format(new Date(activity.date), 'HH:mm', { locale: fr }),
                  })
                : t('confirmUnregistrationText')}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, md: 3 } }}>
          <Button 
            onClick={handleCloseDialog}
            sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}
          >
            {t('cancel')}
          </Button>
          <Button 
            onClick={dialogType === 'register' ? handleRegister : handleUnregister}
            variant="contained"
            color={dialogType === 'register' ? 'primary' : 'error'}
            disabled={loading || (activity.category === 'Soin' && !registrationData.treatmentType)}
            sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}
          >
            {loading ? (
              <CircularProgress size={24} />
            ) : (
              dialogType === 'register' ? t('register') : t('unregister')
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modify Participants Dialog */}
      {activity && (
        <ModifyParticipantsDialog
          open={openModifyDialog}
          onClose={() => setOpenModifyDialog(false)}
          onModify={handleModifyParticipants}
          currentParticipants={userRegistration?.numberOfParticipants || 0}
          maxParticipants={activity.maxParticipants}
          currentRegistrations={activity.currentParticipants}
          category={activity.category}
          error={modifyError}
        />
      )}
    </Container>
  );
};

export default ActivityDetails; 